import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import CloseIcon from '@/shared/ui/icons/CloseIcon';

export type ChipVariant = 'default' | 'error';

export type ChipProps = {
	label: string;
	variant?: ChipVariant;
	maxWidth?: string;
	className?: string;
	notranslate?: boolean;
	onRemove?: (event: React.MouseEvent | React.KeyboardEvent) => void;
	removeAriaLabel?: string;
};

type VariantStyle = {
	container: string;
	text: string;
	icon: string;
};

const variantStyles: Record<ChipVariant, VariantStyle> = {
	default: {
		container: 'bg-secondary-ultra-light',
		text: 'text-secondary',
		icon: 'text-secondary',
	},
	error: {
		container: 'bg-error-light',
		text: 'text-error',
		icon: 'text-error',
	},
};

const Chip = ({
	label,
	variant = 'default',
	maxWidth,
	className,
	notranslate = false,
	onRemove,
	removeAriaLabel,
}: ChipProps) => {
	const variantStyle = variantStyles[variant];

	const handleRemove = (event: React.MouseEvent | React.KeyboardEvent) => {
		event.stopPropagation();
		onRemove?.(event);
	};

	return (
		<span
			className={twMerge(
				clsx(
					'inline-flex items-center gap-2 rounded-full px-3 py-1 w-max max-w-full',
					variantStyle.container,
					className,
				),
			)}
		>
			<span
				translate={notranslate ? 'no' : undefined}
				className={clsx(
					'truncate text-base first-letter:uppercase',
					variantStyle.text,
					notranslate && 'notranslate',
				)}
				style={maxWidth ? { maxWidth } : undefined}
			>
				{label}
			</span>
			{onRemove && (
				<span
					role='button'
					tabIndex={0}
					aria-label={removeAriaLabel}
					onClick={handleRemove}
					onKeyDown={(event) => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault();
							handleRemove(event);
						}
					}}
					className={clsx('inline-flex shrink-0 cursor-pointer', variantStyle.icon)}
				>
					<CloseIcon
						height={16}
						width={16}
					/>
				</span>
			)}
		</span>
	);
};

export default Chip;
